import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { StudentAccessResult } from '../services/classCodeService';
import { ClassMembersPartitionService } from '../services/classMembersPartitionService';

interface AccessCodeChangeModalProps {
  isOpen: boolean;
  resultData: StudentAccessResult | null;
  onClose: () => void;
  onSuccess: (newAccessCode: string) => void;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const ModalContent = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-large);
  z-index: 10002;
  border: var(--modal-border);
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  color: var(--color-text-primary);
  font-size: 1.3rem;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: var(--color-text-primary);
  font-weight: bold;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: rgba(187, 187, 187, 1);
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 0.9rem;
  margin-top: 6px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.$primary ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  background: ${props => props.$primary ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
  color: ${props => props.$primary ? '#fff' : 'var(--color-text-primary)'};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$primary ? 'var(--color-accent)' : 'var(--color-border)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AccessCodeChangeModal: React.FC<AccessCodeChangeModalProps> = ({
  isOpen,
  resultData,
  onClose,
  onSuccess
}) => {
  const [newAccessCode, setNewAccessCode] = useState('');
  const [confirmAccessCode, setConfirmAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // 모달이 열릴 때 현재 입장코드로 초기화
  useEffect(() => {
    if (isOpen && resultData?.student) {
      const currentAccessCode = resultData.student.basicInfo.accessCode || '';
      setNewAccessCode(currentAccessCode);
      setConfirmAccessCode('');
      setError('');
    }
  }, [isOpen, resultData]);

  const handleSave = async () => {
    setError('');

    // 유효성 검사
    if (newAccessCode.trim() === '') {
      setError('입장코드를 입력하세요.');
      return;
    }

    if (newAccessCode !== confirmAccessCode) {
      setError('입장코드가 일치하지 않습니다.');
      return;
    }

    if (!resultData?.student || !resultData?.classInfo) {
      setError('학생 정보를 찾을 수 없습니다.');
      return;
    }

    setLoading(true);

    try {
      const { userId, classId } = resultData.classInfo;
      const studentId = resultData.student.basicInfo.id;

      const partitionService = new ClassMembersPartitionService(userId, classId);
      await partitionService.updateAccessCode(studentId, newAccessCode.trim());

      // 성공 시 모달 닫기 및 콜백 호출
      onSuccess(newAccessCode.trim());
    } catch (err: any) {
      console.error('입장코드 변경 실패:', err);
      setError(err.message || '입장코드 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const student = resultData?.student;
  const isGuestMode = resultData?.isGuestMode || false;

  if (isGuestMode || !student || !resultData?.classInfo) {
    return null;
  }

  return (
    <ModalOverlay isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyPress}>
        <Title>입장코드 변경</Title>
        
        <FormGroup>
          <Label>새 입장코드</Label>
          <Input
            type="text"
            value={newAccessCode}
            onChange={(e) => setNewAccessCode(e.target.value)}
            placeholder="입장코드를 입력하세요"
            disabled={loading}
            autoFocus
          />
        </FormGroup>

        <FormGroup>
          <Label>입장코드 확인</Label>
          <Input
            type="text"
            value={confirmAccessCode}
            onChange={(e) => setConfirmAccessCode(e.target.value)}
            placeholder="입장코드를 다시 입력하세요"
            disabled={loading}
            onKeyPress={handleKeyPress}
          />
        </FormGroup>

        {error && <ErrorText>{error}</ErrorText>}

        <ButtonRow>
          <Button onClick={onClose} disabled={loading}>
            취소
          </Button>
          <Button $primary onClick={handleSave} disabled={loading}>
            {loading ? '저장 중...' : '저장'}
          </Button>
        </ButtonRow>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AccessCodeChangeModal;

